import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Field, Input } from '@/components/ui/Field'
import { Tag } from '@/components/ui/Tag'
import {
  useCompanyThresholds,
  useSaveThresholds,
} from '@/features/settings/api/useCompanyTemplates'

/* Story 2.9 AC 4: the three thresholds other stories require to be configurable
 * rather than hard-coded, "editable there with its default shown".
 *
 * Until this panel existed the word "threshold" appeared nowhere in
 * frontend/src, while `GET`/`PUT /api/admin/settings/thresholds` had been mounted
 * and tested since migration 0032. Three stories cite these numbers as
 * configurable — FR-16's follow-up and validity-expiry reminders (Story 2.8) and
 * FR-52's stuck-shipment count (Story 6.2) — so "configurable" had a table, an
 * endpoint, a default and no surface.
 *
 * ── THE HONEST PART, stated on the panel rather than only here ─────────────
 * Two of these three numbers are read by nothing yet. `internal/settings`
 * carries that plainly at thresholds.go — "Story 2.8 is what teaches the
 * reminder job to read them" — and no reminder job exists in backend or client.
 * FR-52's stuck days IS read, but the dashboard measures against the
 * environment's ASA_DASHBOARD_STUCK_DAYS rather than against this row.
 *
 * So each field says who consumes it, and the two that nobody consumes say so.
 * `readmodel.go` makes the argument this follows: the dashboard's range picker
 * was REMOVED rather than left inert, because "a picker that visibly changes
 * nothing teaches the founders that the controls are decorative, which is more
 * expensive than an absent control". A settings field is the one place that
 * argument does not lead to deletion — the value must be storable before the job
 * that reads it can be written, and 0032 stored it deliberately ahead of its
 * readers — so the field stays and the panel does not pretend.
 */

/** A day count as typed. Kept as a string so a half-typed field is not
 *  rewritten under the reader's cursor: `Number('')` is 0, and a numeric state
 *  would turn a cleared input into a saved zero. */
interface ThresholdDraft {
  followUpDays: string
  validityWarningDays: string
  stuckShipmentDays: string
}

/** The bounds the server enforces (`settings.dayCount`), repeated here so the
 *  refusal arrives beside the field rather than as a 422 after a round trip.
 *  The server remains the control; this is the courtesy. */
const MIN_DAYS = 1
const MAX_DAYS = 90

/** The reason a day count is refused, as a translation KEY rather than a
 *  sentence.
 *
 *  Returning a key keeps this function pure and keeps `t` out of it — i18next's
 *  `TFunction` is typed against the catalogue, so threading it through a helper
 *  means either widening the helper's signature to `(key: string) => string`,
 *  which throws away the compile-time check that the key exists, or importing a
 *  type nobody else in this file needs. The caller translates. */
type DayProblem =
  | 'common.form.required'
  | 'settings.thresholds.wholeDays'
  | 'settings.thresholds.outOfRange'

function dayProblem(raw: string): DayProblem | undefined {
  const trimmed = raw.trim()
  if (trimmed === '') return 'common.form.required'
  // A day count is whole. `parseInt` would silently accept "3.7" as 3, so the
  // shape is checked before the value.
  if (!/^\d+$/.test(trimmed)) return 'settings.thresholds.wholeDays'
  const value = Number(trimmed)
  if (value < MIN_DAYS || value > MAX_DAYS) return 'settings.thresholds.outOfRange'
  return undefined
}

export function ThresholdsPanel() {
  const { t } = useTranslation()
  const { data, isPending, isError, error, refetch } = useCompanyThresholds()
  const save = useSaveThresholds()

  /* Seeded from the server once loaded, and re-seeded when the identity of the
     loaded row changes. The draft is local because three fields save together:
     a controlled-from-cache input would fight the reader mid-edit. */
  const [draft, setDraft] = useState<ThresholdDraft | null>(null)
  const [touched, setTouched] = useState(false)

  if (isPending) return <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  const current: ThresholdDraft = draft ?? {
    followUpDays: String(data.followUpDays),
    validityWarningDays: String(data.validityWarningDays),
    stuckShipmentDays: String(data.stuckShipmentDays),
  }

  const fields = [
    {
      key: 'followUpDays' as const,
      label: t('settings.thresholds.followUp'),
      hint: t('settings.thresholds.followUpHint'),
      fallback: data.defaultFollowUpDays,
      // FR-16's reminder job does not exist; the field says so rather than
      // implying a number that changes behaviour.
      consumer: t('settings.thresholds.awaitingReminderJob'),
    },
    {
      key: 'validityWarningDays' as const,
      label: t('settings.thresholds.validityWarning'),
      hint: t('settings.thresholds.validityWarningHint'),
      fallback: data.defaultValidityWarningDays,
      consumer: t('settings.thresholds.awaitingReminderJob'),
    },
    {
      key: 'stuckShipmentDays' as const,
      label: t('settings.thresholds.stuckShipment'),
      hint: t('settings.thresholds.stuckShipmentHint'),
      fallback: data.defaultStuckShipmentDays,
      consumer: t('settings.thresholds.readByDashboardEnv'),
    },
  ]

  const problems = fields.map((f) => dayProblem(current[f.key]))
  const invalid = problems.some(Boolean)

  return (
    <Card>
      <CardHeader
        action={
          <Button
            variant="primary"
            size="sm"
            disabled={save.isPending}
            onClick={() => {
              setTouched(true)
              if (invalid) return
              save.mutate(
                {
                  followUpDays: Number(current.followUpDays),
                  validityWarningDays: Number(current.validityWarningDays),
                  stuckShipmentDays: Number(current.stuckShipmentDays),
                },
                // The draft is dropped on success so the panel reads the
                // server's own answer — including `configured`, which is what
                // the hint below switches on.
                { onSuccess: () => { setDraft(null); setTouched(false) } },
              )
            }}
          >
            {t('common.actions.save')}
          </Button>
        }
      >
        <CardTitle>{t('settings.thresholds.title')}</CardTitle>
      </CardHeader>

      <CardBody className="flex flex-col gap-3.5 pt-2">
        {/* Whether these are the defaults or somebody's choice. Read off the
            server's flag rather than compared against the defaults here: a
            founder who saves 3/2/3 has configured them, and a value-comparison
            would report that as untouched. */}
        <p className="text-text-muted text-[12px] leading-snug font-light">
          {data.configured
            ? t('settings.thresholds.configured')
            : t('settings.thresholds.usingDefaults')}
        </p>

        {/* A failed save must say so where it was triggered — there is no global
            mutation error handler, so an unread `.error` is a silent refusal. */}
        {save.error ? (
          <ErrorState error={save.error} onRetry={() => save.reset()} />
        ) : null}

        {fields.map((field, index) => (
          <Field
            key={field.key}
            label={field.label}
            required
            error={touched && problems[index] ? t(problems[index]) : undefined}
            hint={`${field.hint} · ${t('settings.thresholds.defaultIs', { days: field.fallback })} · ${field.consumer}`}
          >
            {(props) => (
              <Input
                // A numeric keyboard on a phone, which is where Operations
                // read settings from (UX-DR-27). `inputMode` rather than
                // type="number", whose spinners and locale-dependent decimal
                // handling this project avoids elsewhere.
                inputMode="numeric"
                value={current[field.key]}
                onChange={(e) =>
                  setDraft({ ...current, [field.key]: e.target.value })
                }
                {...props}
              />
            )}
          </Field>
        ))}

        <div className="flex flex-wrap items-center gap-1.5">
          <Tag>{t('settings.thresholds.range', { min: MIN_DAYS, max: MAX_DAYS })}</Tag>
        </div>
      </CardBody>
    </Card>
  )
}
